
import { ChangeDetectionStrategy, Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DataFilterManagerService, FilterPanelFacadeService } from '../../services';
import { FilterField, FilterFieldWithOptions, OptionState } from '../../models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-selected-filters-list',
  templateUrl: './selected-filters-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectedFiltersListComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly filterFields$ = new BehaviorSubject<FilterField[]>([]);
  protected currentFilterInstanceId: string;

  optionStates$: Observable<OptionState[]>;
  filterFieldsWithOptions$: Observable<FilterFieldWithOptions[]>;
  fieldsToHide$: Observable<string[]>;

  fieldsWithOptions: FilterFieldWithOptions[] | undefined;

  @Input()
  displayedFilterOptionsOnly = true;

  @Input()
  set filterFields(filterFields: FilterField[]) {
    this.filterFields$.next(filterFields || []);
  }

  constructor(
    private filterManagerService: DataFilterManagerService,
    private filterPanelFacadeService: FilterPanelFacadeService,
  ) {}

  ngOnInit(): void {
    this.filterPanelFacadeService
      .getCurrentFilterInstanceId()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filterInstanceId: string) => {
        this.currentFilterInstanceId = filterInstanceId;
      });

    this.fieldsToHide$ = this.filterPanelFacadeService.getCurrentFilterInstanceId().pipe(
      switchMap((filterInstanceId) =>
        this.filterManagerService.getFilteringFields(filterInstanceId).pipe(
          map((fields) => {
            return fields.filter((field) => !field.displayedInSelectedFiltersList).map((field) => field.id);
          }),
        ),
      ),
    );

    this.optionStates$ = combineLatest([
      this.filterPanelFacadeService.getCurrentFilterInstanceId(),
      this.fieldsToHide$,
    ]).pipe(
      switchMap(([filterInstanceId, fieldsToHide]) =>
        this.filterManagerService
          .getAppliedFilterOptions(filterInstanceId, this.displayedFilterOptionsOnly)
          .pipe(
            map((optionStates) => optionStates.filter((optionState) => !fieldsToHide.includes(optionState.fieldId))),
          ),
      ),
    );

    this.filterFieldsWithOptions$ = combineLatest([this.optionStates$, this.filterFields$]).pipe(
      map(([optionStates, filterFields]) => this.buildFilterFieldsWithOptions(optionStates, filterFields)),
    );
  }

  resetAllFilters(): void {
    this.filterManagerService.reset(this.currentFilterInstanceId);
  }

  buildTranslationKey(relativeKey: string): string {
    return `core.dataFilter.${relativeKey}`;
  }

  private buildFilterFieldsWithOptions(
    optionStates: OptionState[],
    filterFields: FilterField[],
  ): FilterFieldWithOptions[] {
    // When we don't provide any filter fields we want to put all the tags without a label
    if (!filterFields.length) {
      return [
        {
          fieldId: '',
          options: optionStates,
        },
      ];
    }

    // Group the options by fieldId otherwise put it on "more filters"
    const moreFilters: FilterFieldWithOptions = {
      fieldId: '',
      translationKey: this.buildTranslationKey('moreFilters'),
      options: [],
    };
    const fieldIdToOptionsMap: Record<string, OptionState[]> = {};
    filterFields.forEach((filterField) => {
      if (!(filterField.fieldId in fieldIdToOptionsMap)) {
        fieldIdToOptionsMap[filterField.fieldId] = [];
      }
    });
    optionStates.forEach((optionState) => {
      if (optionState.fieldId in fieldIdToOptionsMap) {
        fieldIdToOptionsMap[optionState.fieldId].push(optionState);
      } else {
        moreFilters.options.push(optionState);
      }
    });

    // Prepare the result using the groups on the order of filterFields
    const result = filterFields
      .map((filterField) => ({
        ...filterField,
        options: fieldIdToOptionsMap[filterField.fieldId],
      }))
      // We don't want to display empty groups
      .filter((filterField) => filterField.options.length);

    if (moreFilters.options.length) {
      result.push(moreFilters);
    }

    return result;
  }
}
